import React, { useEffect, useState } from "react";
import styled from "styled-components";
import SectionHeading from "../UI/SectionHeading";
import FeatureProductBox from "../UI/FeatureProductBox";
import CategoryLoader from "../Loaders/CategoryLoader/CategoryLoader";
import { Link } from "react-router-dom";
import TopWearIcon from "../../assets/images/category/category-1.png";
import HandbagIcon from "../../assets/images/category/category-2.png";
import FootwearIcon from "../../assets/images/category/category-3.png";
import NecklaceIcon from "../../assets/images/category/category-4.png";

const MainBox = styled.section`
  padding: 1rem 0;
  margin-top: 1rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  overflow-x: hidden;
  position: relative;
`;

const CategoryBox = styled.div`
  width: 60%;
  display: flex;
  justify-content: space-evenly;
  align-items: stretch;
  gap: 1.5rem;
  @media only screen and (max-width: 949px) {
    width: 95%;
    gap: 1rem;
  }
`;

const CategoryButton = styled.button`
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  padding: 1.6rem 1.2rem;
  border-radius: 0.8rem;
  background-color: white;
  border: 1px solid #e5d9c8;
  cursor: pointer;
  min-width: 12rem;
  flex: 1;

  &.active {
    background-color: #fbf3e7;
    border: 1px solid #8a5a2b;
  }

  .cat-icon {
    width: 70px;
    height: auto;
  }

  span {
    text-transform: uppercase;
    font-size: 1.2rem;
    font-weight: 600;
    letter-spacing: 0.05rem;
    color: #3a3a3a;
    text-align: center;
    line-height: 1.4rem;
  }

  @media only screen and (max-width: 949px) {
    padding: 1.2rem 0.6rem;
    min-width: unset;
    .cat-icon {
      width: 50px;
    }
    span {
      font-size: 1.05rem;
    }
  }
`;

const ProductsBox = styled.div`
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem 2rem;
  padding: 1rem 0;

  a {
    text-decoration: none;
    color: inherit;
    display: block;
    width: 100%;
  }

  @media only screen and (max-width: 1200px) {
    width: 92%;
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem 1.5rem;
  }

  @media only screen and (max-width: 768px) {
    width: 95%;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem 1rem;
  }

  @media only screen and (max-width: 480px) {
    width: 95%;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.2rem 0.8rem;
  }
`;

const NoProductsFoundDiv = styled.div`
  grid-column: 1 / -1;
  width: 100%;
  letter-spacing: 0.2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
  color: #a4a1a1;
  height: 30vh;
  font-size: 1.7rem;
  border-radius: 1rem;
`;

const staticCategoryMap = {
  // Handbags
  wallets: "handbags",
  "totes-top-handle": "handbags",
  clutches: "handbags",
  sling: "handbags",
  book: "handbags",
  handbag: "handbags",
  handbags: "handbags",
  bags: "handbags",
  bag: "handbags",

  // Footwear
  "slippers-slides": "footwear",
  sandals: "footwear",
  "mojaris-mules": "footwear",
  "loafers-close-shoe": "footwear",
  heels: "footwear",
  "girls-footwears": "footwear",
  footwear: "footwear",
  footwears: "footwear",
  shoes: "footwear",
  shoe: "footwear",

  // Silver Accessories
  earing: "silver-accessories",
  necklaces: "silver-accessories",
  rings: "silver-accessories",
  "bangles-bracelets": "silver-accessories",
  addons: "silver-accessories",
  contemporary: "silver-accessories",
  festive: "silver-accessories",
  "silver-accessories": "silver-accessories",
  silver: "silver-accessories",
  accessories: "silver-accessories",
  jewellery: "silver-accessories",
  jewelry: "silver-accessories",

  // Woman Top Wear
  "printed-suit": "topwear",
  "jump-suit": "topwear",
  "neck-stone-work-jumpsuit": "topwear",
  "anarkali-suit-sets": "topwear",
  "a-line-top-dhoti-pant": "topwear",
  "kurti-palazzos": "topwear",
  "flared-kurti": "topwear",
  "co-ord-set": "topwear",
  "straight-top": "topwear",
  "flared-dress": "topwear",
  "dress-material": "topwear",
  dupattas: "topwear",
  saree: "topwear",
  kurti: "topwear",
  "women-top-wear": "topwear",
  "women-top": "topwear",
  "women-tops": "topwear",
  topwear: "topwear",
};

const FeatureProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState("topwear");

  useEffect(() => {
    const fetcher = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BASE_URL}/product/all-items`),
          fetch(`${process.env.REACT_APP_BASE_URL}/category/all-category`),
        ]);

        const prodData = await prodRes.json();
        if (prodData.products) {
          setProducts(prodData.products.reverse());
        }

        const catData = await catRes.json();
        if (catData.categories) {
          setCategories(catData.categories);
        }
      } catch (err) {
        console.error("Error fetching products or categories:", err);
      }
      setIsLoading(false);
    };
    fetcher();
  }, []);

  const getProductCategoryGroup = (item) => {
    const cat = item.category ? item.category.toLowerCase().trim() : "";
    const subcat = item.subcategory ? item.subcategory.toLowerCase().trim() : "";

    // 1. Check against dynamic DB categories (slug or title)
    const matched = categories.find(
      (c) =>
        (c.slug && (c.slug.toLowerCase() === cat || c.slug.toLowerCase() === subcat)) ||
        (c.title && (c.title.toLowerCase() === cat || c.title.toLowerCase() === subcat))
    );

    if (matched && matched.parentCategory) {
      const parent = matched.parentCategory.toLowerCase();
      if (parent.includes("handbag") || parent.includes("bag")) return "handbags";
      if (parent.includes("footwear") || parent.includes("shoe")) return "footwear";
      if (
        parent.includes("silver") ||
        parent.includes("accessor") ||
        parent.includes("jewel")
      )
        return "silver-accessories";
      if (parent.includes("top") || parent.includes("wear") || parent.includes("women"))
        return "topwear";
    }

    // 2. Fallback to static category dictionary
    if (staticCategoryMap[cat]) return staticCategoryMap[cat];
    if (staticCategoryMap[subcat]) return staticCategoryMap[subcat];

    // 3. Keyword / partial matching
    if (
      cat.includes("bag") ||
      cat.includes("wallet") ||
      cat.includes("clutch") ||
      cat.includes("sling") ||
      cat.includes("tote")
    ) {
      return "handbags";
    }
    if (
      cat.includes("footwear") ||
      cat.includes("shoe") ||
      cat.includes("sandal") ||
      cat.includes("heel") ||
      cat.includes("slipper") ||
      cat.includes("mojari") ||
      cat.includes("loafer")
    ) {
      return "footwear";
    }
    if (
      cat.includes("silver") ||
      cat.includes("accessor") ||
      cat.includes("jewel") ||
      cat.includes("earing") ||
      cat.includes("necklace") ||
      cat.includes("ring") ||
      cat.includes("bangle") ||
      cat.includes("bracelet")
    ) {
      return "silver-accessories";
    }

    return "topwear";
  };

  const filterProducts = (item, view) => {
    if (item.status === false) return false;
    return getProductCategoryGroup(item) === view;
  };

  const displayedProducts = products.filter((item) =>
    filterProducts(item, currentView)
  );

  return (
    <MainBox>
      <SectionHeading
        data={{ main: "Feature Products", secondary: "Shop by category" }}
      />
      <CategoryBox>
        <CategoryButton
          onClick={() => setCurrentView("topwear")}
          className={`button ${currentView === "topwear" ? "active" : "notActive"}`}
          id="topwear"
        >
          <img src={TopWearIcon} className="cat-icon" alt="Woman Top Wear" />
          <span>Woman Top Wear</span>
        </CategoryButton>
        <CategoryButton
          onClick={() => setCurrentView("handbags")}
          className={`button ${currentView === "handbags" ? "active" : "notActive"}`}
          id="handbags"
        >
          <img src={HandbagIcon} className="cat-icon" alt="Handbags" />
          <span>Handbags</span>
        </CategoryButton>
        <CategoryButton
          onClick={() => setCurrentView("footwear")}
          className={`button ${currentView === "footwear" ? "active" : "notActive"}`}
          id="footwear"
        >
          <img src={FootwearIcon} className="cat-icon" alt="Footwear" />
          <span>Footwear</span>
        </CategoryButton>
        <CategoryButton
          onClick={() => setCurrentView("silver-accessories")}
          className={`button ${currentView === "silver-accessories" ? "active" : "notActive"}`}
          id="silver-accessories"
        >
          <img src={NecklaceIcon} className="cat-icon" alt="Silver Accessories" />
          <span>Silver Accessories</span>
        </CategoryButton>
      </CategoryBox>

      {isLoading && <CategoryLoader />}

      {!isLoading && (
        <ProductsBox id="productBox">
          {displayedProducts.length > 0 ? (
            displayedProducts.map((item) => {
              const colors = item.color ? item.color.split(",") : ["Default"];
              const image = item.featuredImage || (item.images ? item.images.split(" ")[0] : item.img);
              return (
                <Link
                  to={`/product/${item.category}/${item.slug}/${item.id}`}
                  key={item.id + item.title}
                  state={{ productId: `${item.id}` }}
                >
                  <FeatureProductBox
                    data={{ ...item, colors: colors, img: image }}
                  />
                </Link>
              );
            })
          ) : (
            <NoProductsFoundDiv>
              <p>No Products Found in this category...</p>
            </NoProductsFoundDiv>
          )}
        </ProductsBox>
      )}
    </MainBox>
  );
};

export default FeatureProducts;
